import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Flex, Form, Typography } from 'antd';
import { useProject } from '@contexts/Project.context';
import { useConfirm } from '@contexts/Confirm.context';
import SidebarTemplate from '../../../sidebars/sidebarElements/SidebarTemplate';
import FilterList from '@components/propertyFilters/FilterList';
import FilterActions from '@components/propertyFilters/FilterActions';
import useCloseSidebarWhenEmpty from '@hooks/useCloseSidebarWhenEmpty';
import { COLLECTION_SIDEBAR_IDS } from '../constants';

const { Text } = Typography;

/**
 * Per-object filter panel — renders into the modify-layers portal.
 *
 * Filters only: color is edited inline on the object's row in the tree, which is
 * why `CollectionNode` hides the icon that opens this panel for an object with
 * no filterable properties. Nothing else guarantees it has content.
 *
 * Presentational: receives the `filters` hook instance from CollectionResults
 * rather than calling useObjectFilters internally. This keeps filter state
 * owned at the CollectionResults level where useFilteredFeatures can consume it
 * directly without routing through the Zustand store.
 *
 * TODO: Replace createPortal + SidebarTemplate with antd Drawer for built-in
 *       focus management, keyboard support, animations, and consistent styling.
 *       Keeping current pattern for UX consistency with other secondary sidebars.
 */
const ObjectFilter = ({ objectData, filters, histogramValues }) => {
	const { modifyLayersSidebarRef, setActiveSidebar } = useProject();
	const { confirm } = useConfirm();

	const {
		form,
		initialValues,
		effectiveFilterConfigs,
		isDirty,
		isSaving,
		activeFilters,
		dirtyFilters,
		shownCount,
		totalCount,
		clearFilter,
		apply,
		reset,
		handleValuesChange,
	} = filters;

	const handleHide = useCallback(async () => {
		if (!isDirty) return;

		const confirmed = await confirm({
			title: 'Discard changes?',
			message: 'You have unsaved changes. Are you sure you want to close without applying?',
			confirmLabel: 'Discard',
			cancelLabel: 'Keep editing',
		});

		if (confirmed) {
			reset();
		} else {
			// SidebarTemplate already removed the sidebar from activeSidebars
			// before this resolves — re-add it to keep the panel open.
			setActiveSidebar({
				sidebarId: COLLECTION_SIDEBAR_IDS.objectFilter,
				parentId: 'layerView',
			});
		}
	}, [isDirty, confirm, reset, setActiveSidebar]);

	// The selected object can go away under an open panel (deselected on the tree,
	// or dropped by a data change) while this stays mounted.
	useCloseSidebarWhenEmpty(COLLECTION_SIDEBAR_IDS.objectFilter, !!objectData);

	if (!modifyLayersSidebarRef?.current) return null;
	if (!objectData) return null;

	return createPortal(
		// Object switching is handled imperatively by the useEffect in useObjectFilters
		// (watching objectId). Do NOT put a key here expecting it to remount the Form —
		// Form.useForm() lives in useObjectFilters which is called above createPortal,
		// so the form instance is never recreated by a key on a portal child.
		<SidebarTemplate
			zindex="2"
			sidebarId={COLLECTION_SIDEBAR_IDS.objectFilter}
			header={objectData.label ?? 'Object'}
			closeable
			onHide={handleHide}>
			<Flex vertical style={{ height: '100%', overflow: 'hidden' }}>
				{/* Description */}
				<Flex vertical gap={8} style={{ padding: '12px 16px' }}>
					<Text type="secondary" style={{ fontSize: '0.8rem' }}>
						{totalCount} detections in this run &middot; changes preview live on the map
					</Text>
				</Flex>

				{/* Filter fields */}
				<Form
					form={form}
					initialValues={initialValues}
					component={false}
					onValuesChange={handleValuesChange}>
					<Flex
						vertical
						gap={16}
						style={{
							flex: 1,
							overflowY: 'auto',
							scrollbarGutter: 'stable',
							padding: '4px 0 16px',
						}}>
						{effectiveFilterConfigs.length > 0 && (
							<FilterList
								filters={effectiveFilterConfigs}
								activeFilters={activeFilters}
								dirtyFilters={dirtyFilters}
								isDirty={isDirty}
								shownCount={shownCount}
								totalCount={totalCount}
								histogramValues={histogramValues}
								onClear={clearFilter}
							/>
						)}
					</Flex>
				</Form>

				{/* Footer */}
				<FilterActions
					isDirty={isDirty}
					isSaving={isSaving}
					onApply={apply}
					onReset={reset}
				/>
			</Flex>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
};

export default ObjectFilter;
